import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { usePPCAuth, PPC_API_URL } from './PPCContext';

// ── Module config ─────────────────────────────────────────────────────────────
const MODULES = [
  { id: 'romeo', name: 'ROMEO', icon: 'R', desc: 'Romeo Division Dashboard',     route: '/ppc/romeo', grad: 'linear-gradient(135deg,#7b1fa2 0%,#4a148c 100%)', shadow: 'rgba(123,31,162,.35)', dot: '#7b1fa2' },
  { id: 'bravo', name: 'BRAVO', icon: 'B', desc: 'H.ON Processing Dashboard',    route: '/ppc/bravo', grad: 'linear-gradient(135deg,#d32f2f 0%,#1565c0 100%)', shadow: 'rgba(211,47,47,.35)',  dot: '#d32f2f' },
  { id: 'golf',  name: 'GOLF',  icon: 'G', desc: 'Golf Division Dashboard',      route: '/ppc/golf',  grad: 'linear-gradient(135deg,#e65100 0%,#f57c00 100%)', shadow: 'rgba(230,81,0,.35)',   dot: '#e65100' },
  { id: 'papa',  name: 'PAPA',  icon: 'P', desc: 'Product Processing Dashboard', route: '/ppc/papa',  grad: 'linear-gradient(135deg,#1b5e20 0%,#2e7d32 100%)', shadow: 'rgba(27,94,32,.35)',   dot: '#1b5e20' },
];

const TODAY = new Date().toISOString().slice(0, 10);
const fmt   = (v, d = 2) => parseFloat(v || 0).toFixed(d);

// ─────────────────────────────────────────────────────────────────────────────
const DashboardSelector = () => {
  const { user, logout } = usePPCAuth();
  const navigate = useNavigate();
  const isAdmin  = user?.role === 'admin';

  const [activeTab,         setActiveTab]         = useState('romeo');
  const [panelFrom,         setPanelFrom]         = useState(TODAY);
  const [panelTo,           setPanelTo]           = useState(TODAY);
  const [summaryData,       setSummaryData]        = useState({});
  const [contractors,       setContractors]        = useState({});
  const [loadingTab,        setLoadingTab]         = useState(null);
  const [pendingCount,      setPendingCount]       = useState(0);
  const [notifications,     setNotifications]      = useState([]);
  const [unreadCount,       setUnreadCount]        = useState(0);
  const [showNotifications, setShowNotifications]  = useState(false);
  const [toast,             setToast]              = useState(null);

  // No inline add modal — navigate directly to full dashboard

  const notifRef = useRef(null);
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // ── Fetch summary for a module ──────────────────────────────────────────
  const fetchSummary = useCallback(async (modId) => {
    setLoadingTab(modId);
    try {
      const res = await axios.get(`${PPC_API_URL}/${modId}.php?action=summary&date=${panelFrom}&to=${panelTo}`, auth());
      if (res.data.success) setSummaryData(prev => ({ ...prev, [modId]: res.data }));
    } catch (e) { console.error(`fetchSummary ${modId}`, e); }
    finally { setLoadingTab(null); }
  }, []);

  // ── Fetch contractors for panel dropdowns ────────────────────────────────
  const fetchContractors = useCallback(async (modId) => {
    try {
      const res = await axios.get(
        `${PPC_API_URL}/contractors.php?action=by-module&module=${modId.toUpperCase()}`, auth()
      );
      if (res.data.success) setContractors(prev => ({ ...prev, [modId]: res.data.contractors }));
    } catch (e) {}
  }, []);

  // ── Admin data ───────────────────────────────────────────────────────────
  const fetchAdminData = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const [c, n] = await Promise.all([
        axios.get(`${PPC_API_URL}/edit_requests.php?action=pending-count`,  auth()),
        axios.get(`${PPC_API_URL}/edit_requests.php?action=notifications`,  auth()),
      ]);
      if (c.data.success) setPendingCount(c.data.count);
      if (n.data.success) { setNotifications(n.data.notifications || []); setUnreadCount(n.data.unread_count || 0); }
    } catch (e) {}
  }, [isAdmin]);

  const markAllRead = async () => {
    try { await axios.put(`${PPC_API_URL}/edit_requests.php?action=mark-read`, {}, auth()); setUnreadCount(0); fetchAdminData(); } catch (e) {}
  };

  useEffect(() => {
    MODULES.forEach(m => { fetchSummary(m.id); fetchContractors(m.id); });
    fetchAdminData();
    const t = setInterval(fetchAdminData, 30000);
    return () => clearInterval(t);
  }, [fetchSummary, fetchContractors, fetchAdminData]);

  // Re-fetch all summaries when panel date range changes
  useEffect(() => {
    if (panelFrom && panelTo && panelFrom <= panelTo) {
      MODULES.forEach(m => fetchSummary(m.id));
    }
  }, [panelFrom, panelTo]);

  // Close notifications on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifications(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Tab click ────────────────────────────────────────────────────────────
  const handleTabClick = (id) => {
    setActiveTab(id);
    if (!summaryData[id]) fetchSummary(id);
  };

  const activeMod = MODULES.find(m => m.id === activeTab);
  const data      = summaryData[activeTab] || {};
  const ctors     = contractors[activeTab] || [];

  // ── Build table rows ─────────────────────────────────────────────────────
  // Romeo: only show contractors with actual entries (data-driven, no empty rows)
  // Others: show all contractors from master list
  const buildRows = (modId) => {
    const d   = summaryData[modId] || {};
    const rows = d.contractorSummary || [];
    if (modId === 'romeo') {
      // Only return rows that have actual data
      return rows;
    }
    // Other modules: show all from contractor master, zero fill if no data
    const cts = (contractors[modId] || []).map(c => c.name || c);
    const map = {};
    rows.forEach(r => { map[r.contractor] = r; });
    return cts.map(name => map[name] || {
      contractor: name, day_entry_id: null, night_entry_id: null,
      day_trays: 0, night_trays: 0, total_trays: 0, day_qty: 0, night_qty: 0, total_quantity: 0,
      total_hon_qty: 0, total_head_less_qty: 0, day_hon_qty: 0, day_head_less_qty: 0,
      night_hon_qty: 0, night_head_less_qty: 0, total_tokens: 0, total_assoc_male: 0,
    });
  };

  const rows = buildRows(activeTab);

  // ── Stat strip renderer ───────────────────────────────────────────────────
  const renderStats = () => {
    const s = data;
    // FIX B6: compute ROMEO stats from contractorSummary — s.dayTotalTrays etc. don't exist in API response
    const romeoRows      = s.contractorSummary || [];
    const romeoTrays     = romeoRows.reduce((acc, r) => acc + parseInt(r.total_trays      || 0), 0);
    const romeoQty       = romeoRows.reduce((acc, r) => acc + parseFloat(r.total_quantity || 0), 0);
    const romeoMaleAssoc = romeoRows.reduce((acc, r) => acc + parseInt(r.male_associates  || 0), 0);
    const romeoSuppMale  = romeoRows.reduce((acc, r) => acc + parseInt(r.supporting_male  || 0), 0);
    const romeoTphSum    = romeoRows.reduce((acc, r) => acc + parseFloat(r.trays_per_hour || 0), 0);
    const romeoAvgTph    = romeoRows.length > 0 ? romeoTphSum / romeoRows.length : 0;

    const cells = {
      romeo: [
        { label: 'Total Trays',        value: romeoTrays.toLocaleString(),          color: '#7b1fa2' },
        { label: 'Total Qty',          value: `${fmt(romeoQty)} KG`,                color: '#059669' },
        { label: 'Associates (Male)',  value: romeoMaleAssoc.toLocaleString(),       color: '#1565c0' },
        { label: 'Support Associates', value: romeoSuppMale.toLocaleString(),        color: '#d97706' },
        { label: 'Trays / Hour',       value: fmt(romeoAvgTph),                     color: '#7b1fa2' },
      ],
      bravo: [
        { label: 'Day Target',    value: `${fmt(s.dayTarget)} KG`,               color: '#1565c0' },
        { label: 'Total Head ON', value: `${fmt(s.dayTotal)} KG`,                color: '#059669' },
        { label: 'Total Head Less',value:`${fmt(s.dayHeadLessTotal)} KG`,        color: '#059669' },
        { label: 'Balance',       value: `${fmt((s.dayTarget||0)-(s.dayTotal||0))} KG`, color: '#d97706' },
        { label: 'Total Tokens',  value: s.dayTokens || 0,                       color: '#d97706' },
      ],
      golf: [
        { label: 'Total Lots',    value: s.totalLots || 0,                       color: '#d97706' },
        { label: 'H.ON Qty',      value: `${fmt(s.totalHonQty)} KG`,             color: '#059669' },
        { label: 'H.L Qty',       value: `${fmt(s.totalHlQty)} KG`,             color: '#1565c0' },
        { label: 'Yield %',       value: `${fmt(s.yieldPercent || 0, 1)}%`,      color: '#059669' },
        { label: 'Associates',    value: s.totalAssociates || 0,                 color: '#1565c0' },
      ],
      papa: [
        { label: 'Day Target',    value: `${fmt(s.dayTarget)} KG`,               color: '#1565c0' },
        { label: 'Total Output',  value: `${fmt(s.dayTotal)} KG`,                color: '#059669' },
        { label: 'Balance',       value: `${fmt((s.dayTarget||0)-(s.dayTotal||0))} KG`, color: '#d97706' },
        { label: 'Associates',    value: s.dayAssociates || 0,                   color: '#1565c0' },
        { label: 'Total Tokens',  value: s.dayTokens || 0,                       color: '#d97706' },
      ],
    };
    return (cells[activeTab] || []).map((c, i) => (
      <div key={i} style={panelStatCell(i === 4)}>
        <div style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '5px' }}>{c.label}</div>
        <div style={{ fontSize: '17px', fontWeight: 700, fontFamily: "'DM Mono',monospace", color: c.color }}>{c.value}</div>
      </div>
    ));
  };

  // ── Table column headers per module ──────────────────────────────────────
  const renderTableHeaders = () => {
    const base = <><th style={th('left')}>Contractor</th><th style={th()}>Status</th></>;
    if (activeTab === 'romeo') return <><th style={th('left')}>Contractor</th><th style={{ ...th(), color: '#7b1fa2', fontWeight:700 }}>Line</th><th style={{ ...th(), color: '#1565c0' }}>Male Assoc</th><th style={{ ...th(), color: '#d97706' }}>Supp Assoc</th><th style={{ ...th(), background: 'rgba(211,47,47,.08)', color: '#d32f2f' }}>Day Trays</th><th style={{ ...th(), background: 'rgba(211,47,47,.08)', color: '#d32f2f' }}>Day Qty</th><th style={{ ...th(), background: 'rgba(21,101,192,.08)', color: '#1565c0' }}>Night Trays</th><th style={{ ...th(), background: 'rgba(21,101,192,.08)', color: '#1565c0' }}>Night Qty</th><th style={{ ...th(), background: '#f3e5f5', color: '#7b1fa2' }}>Total Trays</th><th style={{ ...th(), background: '#dbeafe', color: '#1565c0' }}>Total Qty</th><th style={th()}>Status</th><th style={th()}>Actions</th></>;
    if (activeTab === 'bravo') return <><th style={th('left')}>Contractor</th><th style={th()}>Tokens</th><th style={{ ...th(), background: 'rgba(211,47,47,.08)', color: '#d32f2f' }}>Day H.ON</th><th style={{ ...th(), background: 'rgba(211,47,47,.08)', color: '#d32f2f' }}>Day H.Less</th><th style={{ ...th(), background: 'rgba(21,101,192,.08)', color: '#1565c0' }}>Night H.ON</th><th style={{ ...th(), background: 'rgba(21,101,192,.08)', color: '#1565c0' }}>Night H.Less</th><th style={{ ...th(), background: '#dbeafe', color: '#1565c0' }}>Total H.ON</th><th style={{ ...th(), background: '#d1fae5', color: '#059669' }}>Total H.Less</th><th style={th()}>Status</th><th style={th()}>Actions</th></>;
    if (activeTab === 'golf')  return <><th style={th('left')}>Section</th><th style={th()}>Lots</th><th style={{ ...th(), background: '#d1fae5', color: '#059669' }}>H.ON Qty</th><th style={{ ...th(), background: '#dbeafe', color: '#1565c0' }}>H.L Qty</th><th style={th()}>Yield %</th><th style={th()}>Status</th><th style={th()}>Actions</th></>;
    if (activeTab === 'papa')  return <><th style={th('left')}>Contractor</th><th style={th()}>Assoc</th><th style={{ ...th(), background: 'rgba(211,47,47,.08)', color: '#d32f2f' }}>Day Output</th><th style={{ ...th(), background: 'rgba(21,101,192,.08)', color: '#1565c0' }}>Night Output</th><th style={{ ...th(), background: '#d1fae5', color: '#059669' }}>Total Output</th><th style={th()}>Rate</th><th style={th()}>Status</th><th style={th()}>Actions</th></>;
    return base;
  };

  // ── Table row cells per module ────────────────────────────────────────────
  const renderRowCells = (row) => {
    const hasDay   = !!row.day_entry_id;
    const hasNight = !!row.night_entry_id;
    const hasAny   = hasDay || hasNight;
    const hasBoth  = hasDay && hasNight;
    const statusEl = hasAny
      ? hasBoth ? <span style={{ color: '#059669', fontSize: '11px', fontWeight: 600 }}>✓ Both</span>
                : <span style={{ color: '#d97706', fontSize: '11px', fontWeight: 600 }}>⚡ {hasDay ? 'Day' : 'Night'}</span>
      : <span style={{ color: '#9ca3af', fontSize: '11px' }}>— Pending</span>;
    const actCell = (
      <td style={{ padding: '7px 10px' }}>
        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
          {!hasBoth && <button onClick={() => navigate(`${activeMod.route}?add=1&contractor=${encodeURIComponent(row.contractor)}&shift=${hasDay ? 'NIGHT' : 'DAY'}`)} style={actBtn('#059669')}>ADD</button>}
          {hasAny   && <button onClick={() => navigate(`${activeMod.route}?edit=1&contractor=${encodeURIComponent(row.contractor)}`)} style={actBtn('#1565c0')}>EDIT</button>}
          {hasAny && isAdmin && <button onClick={() => navigate(activeMod.route)} style={actBtn('#d32f2f')}>DEL</button>}
        </div>
      </td>
    );
    if (activeTab === 'romeo') return <><td style={{ ...tdStyle(), textAlign: 'left', fontWeight: 700, paddingLeft: '14px', fontSize: '12px', letterSpacing: '.3px', color: '#1a1d23' }}>{row.contractor}</td><td style={{ ...tdStyle(), textAlign:'center', fontWeight:700, color:'#7b1fa2', fontSize:'11px' }}>{row.line}</td><td style={{ ...tdStyle(), color:'#1565c0' }}>{row.male_associates || 0}</td><td style={{ ...tdStyle(), color:'#d97706', fontWeight:600 }}>{row.supporting_male || 0}</td><td style={tdStyle()}>{row.day_trays   || '—'}</td><td style={{ ...tdStyle(), color: '#059669' }}>{row.day_qty   ? fmt(row.day_qty)   : '—'}</td><td style={tdStyle()}>{row.night_trays || '—'}</td><td style={{ ...tdStyle(), color: '#059669' }}>{row.night_qty ? fmt(row.night_qty) : '—'}</td><td style={{ ...tdStyle(), background: '#f3e5f5', color: '#7b1fa2', fontWeight: 700 }}>{row.total_trays || 0}</td><td style={{ ...tdStyle(), background: '#dbeafe', color: '#1565c0', fontWeight: 700 }}>{fmt(row.total_quantity)}</td><td style={{ ...tdStyle(), textAlign: 'center' }}>{statusEl}</td>{actCell}</>;
    if (activeTab === 'bravo') return <><td style={{ ...tdStyle(), textAlign: 'left', fontWeight: 700, paddingLeft: '14px', fontSize: '12px', letterSpacing: '.3px', color: '#1a1d23' }}>{row.contractor}</td><td style={{ ...tdStyle(), color: '#d97706', fontWeight: 600 }}>{row.total_tokens || 0}</td><td style={tdStyle()}>{hasDay ? fmt(row.day_hon_qty) : '—'}</td><td style={{ ...tdStyle(), color: '#059669' }}>{hasDay ? fmt(row.day_head_less_qty) : '—'}</td><td style={tdStyle()}>{hasNight ? fmt(row.night_hon_qty) : '—'}</td><td style={{ ...tdStyle(), color: '#059669' }}>{hasNight ? fmt(row.night_head_less_qty) : '—'}</td><td style={{ ...tdStyle(), background: '#dbeafe', color: '#1565c0', fontWeight: 700 }}>{fmt(row.total_hon_qty)}</td><td style={{ ...tdStyle(), background: '#d1fae5', color: '#059669', fontWeight: 700 }}>{fmt(row.total_head_less_qty)}</td><td style={{ ...tdStyle(), textAlign: 'center' }}>{statusEl}</td>{actCell}</>;
    if (activeTab === 'golf')  return <><td style={{ ...tdStyle(), textAlign: 'left', fontWeight: 700, paddingLeft: '14px', fontSize: '12px', letterSpacing: '.3px', color: '#1a1d23' }}>{row.contractor}</td><td style={tdStyle()}>{row.total_lots || 0}</td><td style={{ ...tdStyle(), color: '#059669' }}>{fmt(row.total_hon_quantity)}</td><td style={{ ...tdStyle(), color: '#1565c0' }}>{fmt(row.total_hl_quantity)}</td><td style={tdStyle()}>{fmt(row.yield_percent || 0, 1)}%</td><td style={{ ...tdStyle(), textAlign: 'center' }}>{statusEl}</td>{actCell}</>;
    if (activeTab === 'papa')  return <><td style={{ ...tdStyle(), textAlign: 'left', fontWeight: 700, paddingLeft: '14px', fontSize: '12px', letterSpacing: '.3px', color: '#1a1d23' }}>{row.contractor}</td><td style={tdStyle()}>{row.total_associates || 0}</td><td style={{ ...tdStyle(), color: '#d32f2f' }}>{hasDay ? fmt(row.day_output) : '—'}</td><td style={{ ...tdStyle(), color: '#1565c0' }}>{hasNight ? fmt(row.night_output) : '—'}</td><td style={{ ...tdStyle(), background: '#d1fae5', color: '#059669', fontWeight: 700 }}>{fmt(row.total_output)}</td><td style={tdStyle()}>₹{fmt(row.rate_per_kg)}</td><td style={{ ...tdStyle(), textAlign: 'center' }}>{statusEl}</td>{actCell}</>;
    return null;
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#f0f2f5', fontFamily: "'DM Sans',sans-serif" }}>

      {/* ── Header ── */}
      <header style={{ background: 'linear-gradient(135deg,#d32f2f 0%,#1565c0 100%)', padding: '0 24px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 16px rgba(211,47,47,.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button onClick={() => navigate('/modules')} style={hdrBtn}>← Modules</button>
          <span style={{ color: '#fff', fontSize: '17px', fontWeight: 700 }}>PPC Dashboard</span>
          {isAdmin && pendingCount > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '3px 8px', borderRadius: '10px' }}>{pendingCount} pending</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isAdmin && (
            <div style={{ position: 'relative' }} ref={notifRef}>
              <button onClick={() => setShowNotifications(!showNotifications)} style={{ ...hdrBtn, position: 'relative' }}>
                🔔 {unreadCount > 0 && <span style={{ background: '#ef4444', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '8px', marginLeft: '4px' }}>{unreadCount}</span>}
              </button>
              {showNotifications && (
                <div style={{ position: 'absolute', top: '110%', right: 0, width: '340px', maxHeight: '360px', overflowY: 'auto', background: '#fff', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,.15)', zIndex: 200, border: '1px solid #e2e6ea' }}>
                  <div style={{ padding: '11px 16px', borderBottom: '1px solid #e2e6ea', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8f9fa' }}>
                    <span style={{ fontWeight: 600, fontSize: '13px' }}>Notifications</span>
                    {unreadCount > 0 && <button onClick={markAllRead} style={{ background: 'none', border: 'none', color: '#1565c0', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit' }}>Mark all read</button>}
                  </div>
                  {notifications.length === 0
                    ? <div style={{ padding: '28px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>No notifications</div>
                    : notifications.map((n, i) => (
                      <div key={i} onClick={() => navigate('/ppc/edit-requests')} style={{ padding: '11px 16px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', background: n.is_read ? '#fff' : '#e3f2fd', borderLeft: n.is_read ? 'none' : '3px solid #1565c0' }}>
                        <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: '2px' }}>{n.title}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{n.message}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                    ))
                  }
                </div>
              )}
            </div>
          )}
          <span style={{ ...hdrBtn, cursor: 'default' }}>{isAdmin ? '👑' : '👤'} {user?.username || user?.full_name}</span>
          <button onClick={logout} style={hdrBtn}>Logout</button>
        </div>
      </header>

      {/* ── Welcome ── */}
      <div style={{ padding: '28px 24px 0', textAlign: 'center' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '5px' }}>Welcome back, {user?.username || user?.full_name}!</h2>
        <p style={{ fontSize: '13px', color: '#6b7280' }}>Select a dashboard · Today: {TODAY}</p>
      </div>

      {/* ── Module Tabs ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', padding: '20px 24px', flexWrap: 'wrap' }}>
        {MODULES.map(m => (
          <button key={m.id} onClick={() => handleTabClick(m.id)}
            style={{ width: '190px', border: 'none', borderRadius: '16px', padding: '22px 16px 18px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', background: m.grad,
              boxShadow: activeTab === m.id ? `0 10px 28px ${m.shadow}` : `0 4px 16px ${m.shadow}`,
              transform: activeTab === m.id ? 'translateY(-4px)' : 'translateY(0)',
              opacity: activeTab === m.id ? 1 : 0.82, transition: 'all .2s',
              position: 'relative', overflow: 'hidden',
              outline: activeTab === m.id ? '3px solid rgba(255,255,255,.6)' : 'none', outlineOffset: '2px' }}>
            {activeTab === m.id && <span style={{ position: 'absolute', bottom: '-1px', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderBottom: '10px solid #f0f2f5' }} />}
            <span style={{ position: 'absolute', top: '12px', right: '14px', color: 'rgba(255,255,255,.6)', fontSize: '16px' }}>→</span>
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(255,255,255,.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: 700, color: '#fff' }}>{m.icon}</div>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#fff', letterSpacing: '.8px' }}>{m.name}</div>
            <div style={{ fontSize: '11px', color: 'rgba(255,255,255,.82)', textAlign: 'center' }}>{m.desc}</div>
          </button>
        ))}
      </div>

      {/* ── Summary Panel ── */}
      {activeMod && (
        <div style={{ margin: '0 24px 28px', background: '#fff', borderRadius: '16px', border: '1px solid #e2e6ea', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,.08)' }}>

          {/* Panel header */}
          <div style={{ padding: '13px 18px', borderBottom: '1px solid #e2e6ea', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', background: '#fafbfc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: activeMod.dot, display: 'inline-block', flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: 700 }}>
                  {activeMod.name} — {panelFrom === TODAY && panelTo === TODAY ? "Today's Summary" : panelFrom === panelTo ? `Summary — ${panelFrom}` : `Summary — ${panelFrom} to ${panelTo}`}
                </div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  {rows.filter(r => r.day_entry_id || r.night_entry_id).length} contractor{rows.filter(r => r.day_entry_id || r.night_entry_id).length !== 1 ? 's' : ''} entered
                </div>
              </div>
              {/* Date range selector */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f0f2f5', borderRadius: '8px', padding: '5px 12px', border: '1px solid #e2e6ea', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>📅 From:</span>
                <input
                  type="date"
                  value={panelFrom}
                  max={panelTo || TODAY}
                  onChange={e => setPanelFrom(e.target.value)}
                  style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, color: '#1a1d23', cursor: 'pointer', outline: 'none', width: '130px' }}
                />
                <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, whiteSpace: 'nowrap' }}>To:</span>
                <input
                  type="date"
                  value={panelTo}
                  min={panelFrom}
                  max={TODAY}
                  onChange={e => setPanelTo(e.target.value)}
                  style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, color: '#1a1d23', cursor: 'pointer', outline: 'none', width: '130px' }}
                />
                {(panelFrom !== TODAY || panelTo !== TODAY) && (
                  <button
                    onClick={() => { setPanelFrom(TODAY); setPanelTo(TODAY); }}
                    style={{ padding: '3px 10px', border: 'none', borderRadius: '5px', background: activeMod.grad, color: '#fff', fontSize: '10px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    Today
                  </button>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => navigate(activeMod.route)}
                style={{ padding: '8px 14px', border: 'none', borderRadius: '8px', background: '#059669', color: '#fff', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                ＋ Add Entry
              </button>
              <button onClick={() => navigate(activeMod.route)}
                style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', background: activeMod.grad, color: '#fff', fontFamily: 'inherit', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>
                Open Full Dashboard →
              </button>
            </div>
          </div>

          {/* Stat strip */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', borderBottom: '1px solid #e2e6ea' }}>
            {renderStats()}
          </div>

          {/* Loading */}
          {loadingTab === activeTab && (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Loading...</div>
          )}

          {/* Table */}
          {!loadingTab && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '700px' }}>
                <thead><tr>{renderTableHeaders()}</tr></thead>
                <tbody>
                  {rows.length === 0
                    ? <tr><td colSpan={10} style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>No entries yet for today · Click + Add Entry to get started</td></tr>
                    : rows.map((row, i) => <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }} onMouseEnter={e => e.currentTarget.style.background = '#f5f7ff'} onMouseLeave={e => e.currentTarget.style.background = ''}>{renderRowCells(row)}</tr>)
                  }
                </tbody>
              </table>
            </div>
          )}

          {/* Panel footer */}
          <div style={{ padding: '10px 18px', background: '#fafbfc', borderTop: '1px solid #e2e6ea', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#6b7280' }}>
            <span>Today only · Daily / Weekly / Monthly available in full dashboard</span>
            <span onClick={() => navigate(activeMod.route)} style={{ color: activeMod.dot, fontWeight: 600, cursor: 'pointer' }}>Open {activeMod.name} Full Dashboard →</span>
          </div>
        </div>
      )}

      {/* ── Admin section ── */}
      {isAdmin && (
        <div style={{ margin: '0 24px 32px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Admin Panel</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
            {[
              { icon: '👷', title: 'Contractor Master', desc: 'Manage contractors & module access', route: '/ppc/contractor-master' },
              { icon: '👥', title: 'User Management',   desc: 'Manage users and permissions',       route: '/ppc/users' },
              { icon: '📝', title: 'Edit Requests',     desc: 'Review pending edit requests',       route: '/ppc/edit-requests', badge: pendingCount },
            ].map((a, i) => (
              <div key={i} onClick={() => navigate(a.route)}
                style={{ background: '#fff', borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', border: '1px solid #e2e6ea', position: 'relative', transition: 'all .2s' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
                <span style={{ fontSize: '26px' }}>{a.icon}</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '2px' }}>{a.title}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{a.desc}</div>
                </div>
                {a.badge > 0 && <span style={{ position: 'absolute', top: '10px', right: '12px', background: '#ef4444', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px' }}>{a.badge}</span>}
              </div>
            ))}
          </div>
        </div>
      )}



      {/* ── Toast ── */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '22px', right: '22px', padding: '11px 18px', borderRadius: '9px', background: toast.type === 'success' ? '#059669' : '#d32f2f', color: '#fff', fontSize: '13px', fontWeight: 500, zIndex: 999, boxShadow: '0 4px 18px rgba(0,0,0,.28)' }}>
          {toast.msg}
        </div>
      )}

      <footer style={{ background: '#fff', borderTop: '1px solid #e2e6ea', padding: '14px', textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
        © 2024 Choice Group — PPC Dashboard
      </footer>

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');`}</style>
    </div>
  );
};

// ── Style helpers ────────────────────────────────────────────────────────────
const hdrBtn   = { background: 'rgba(255,255,255,.18)', border: '1px solid rgba(255,255,255,.28)', color: '#fff', padding: '6px 14px', borderRadius: '20px', cursor: 'pointer', fontSize: '12px', fontFamily: 'inherit', fontWeight: 500 };
const th       = (align = 'center') => ({ padding: '8px 12px', background: '#f8f9fa', borderBottom: '1px solid #e2e6ea', fontWeight: 600, fontSize: '11px', color: '#6b7280', textAlign: align, whiteSpace: 'nowrap' });
const tdStyle  = () => ({ padding: '9px 12px', textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '11px' });
const actBtn   = (bg) => ({ padding: '3px 8px', border: 'none', borderRadius: '4px', background: bg, color: '#fff', fontSize: '10px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' });
const panelStatCell = (last) => ({ padding: '14px 16px', borderRight: last ? 'none' : '1px solid #e2e6ea' });

export default DashboardSelector;
